import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FAMILIES } from "../data/catalog";
import { Portfolio } from "./landing/Portfolio";
import { SegmentedPathways } from "./landing/SegmentedPathways";
import { Hero } from "./landing/Hero";
import { FieldEvidenceVideos } from "./landing/FieldEvidenceVideos";
import {
  ArrowRight,
  ArrowUpRight,
  Activity,
  Boxes,
  Cpu,
  Server,
  QrCode,
  Radio,
  Network,
  Gauge,
  ShieldCheck,
  Workflow,
  LayoutGrid,
  Layers,
  Wrench,
  Lock,
  Zap,
  AlertTriangle,
  CircleDot,
  MapPin,
  Wifi,
  Hash,
  Settings2,
  Cable,
  Building2,
  Plane,
  Truck,
  Factory,
  CircuitBoard,
  ChevronRight,
  ChevronLeft,
  Play,
  Hammer,
  Package,
  Handshake,
  TrendingUp,
  Headphones,
  KeyRound,
  Smartphone,
  IndianRupee,
  HardHat,
  Home as HomeIcon,
  X,
  Menu as MenuIcon,
} from "lucide-react";

/* ===================== ASSETS ===================== */
const MEDIA = {
  hero_barrier: "/branded/hero.png",
  hero_bg:
    "https://static.prod-images.emergentagent.com/jobs/9b82d871-4b50-41f8-8598-25731f623180/images/bd688771252d0408ab00aa6b67c94be9ef1b94376d1ffc634167affbaccb9508.png",
  dashboard:
    "https://static.prod-images.emergentagent.com/jobs/9b82d871-4b50-41f8-8598-25731f623180/images/f19e73c3e35bee3a5940cd32c2cf03e32c3e7c0fd7fd4510f2a25caff823b9e5.png",
  canopy: "/branded/bbc-pro.png",
  smartcity: "/branded/industry-commercial.png",
  metal: "/branded/industry-industrial.png",
};

const TRUST_QR_URL = "/trust/qr-code.png";

/* Locally-hosted BBC Trust app screenshots — served from /public/trust/*.png
   so the site doesn't depend on the customer-assets CDN (some browsers/networks
   were failing on URLs with special characters like %E2%80%AF). */
const TRUST_APP_SCREENS = [
  {
    src: "/trust/01-find-your-barrier.png",
    title: "Find Your Barrier",
    caption: "Search or browse barriers by serial number — every unit indexed in BBC Trust.",
  },
  {
    src: "/trust/02-basic-details.png",
    title: "Basic Details & QC Status",
    caption: "Manufacture date, variant, controller manual, QC status & the person who tested it.",
  },
  {
    src: "/trust/03-inclusions.png",
    title: "Component Inclusions",
    caption: "Motor SR, SMPS SR, control card, spring balancing, MCB — the entire BOM per unit.",
  },
  {
    src: "/trust/04-motor-performance.png",
    title: "Motor Performance & Test Videos",
    caption: "Voltage, power, tested load, open / close speed and the actual factory test video.",
  },
];

/* ===================== PRIMITIVES ===================== */

const Overline = ({ children, icon: Icon }) => (
  <span className="inline-flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-[0.22em] text-[#00FF85]">
    {Icon && <Icon size={12} />}
    <span className="text-[#A1A1AA]">{children}</span>
  </span>
);

const SectionLabel = ({ id, label }) => (
  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#52525B]">
    /{String(id).padStart(2, "0")} — {label}
  </span>
);

const openLeadModal = (type = "sales", source = "platform") => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("bbc:open-lead", { detail: { type, source } })
  );
};

const ButtonPrimary = ({ children, href, onClick, testId, lead, source }) => (
  <a
    href={lead ? undefined : (href || "#")}
    role={lead ? "button" : undefined}
    tabIndex={lead ? 0 : undefined}
    onClick={(e) => {
      if (lead) {
        e.preventDefault();
        openLeadModal(lead, source || testId || "platform");
      }
      if (onClick) onClick(e);
    }}
    data-testid={testId}
    className="group cursor-pointer inline-flex items-center gap-3 bg-[#00FF85] hover:bg-[#00CC6A] text-white font-semibold px-7 py-4 text-sm uppercase tracking-wider transition-all shadow-[0_0_24px_rgba(0,255,133,0.35)] hover:shadow-[0_0_36px_rgba(0,255,133,0.6)]"
  >
    {children}
    <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
  </a>
);

const ButtonGhost = ({ children, href, onClick, testId, lead, source }) => (
  <a
    href={lead ? undefined : (href || "#")}
    role={lead ? "button" : undefined}
    tabIndex={lead ? 0 : undefined}
    onClick={(e) => {
      if (lead) {
        e.preventDefault();
        openLeadModal(lead, source || testId || "platform");
      }
      if (onClick) onClick(e);
    }}
    data-testid={testId}
    className="group cursor-pointer inline-flex items-center gap-3 border border-white/15 hover:border-white text-white font-semibold px-7 py-4 text-sm uppercase tracking-wider hover:bg-white/5 transition-all"
  >
    {children}
    <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
  </a>
);

/* ===================== NAVBAR ===================== */

const NAV = [
  { href: "#platform", label: "How It's Built" },
  { href: "#traceability", label: "BBC Trust" },
  { to: "/products", label: "Products" },
  { href: "#partners", label: "Partners" },
  { to: "/insights", label: "Insights" },
];

export const PlatformNavbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header
      data-testid="platform-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all ${
        scrolled
          ? "bg-[#0F1113]/85 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-3" data-testid="platform-nav-logo">
          <div className="relative w-9 h-9 border border-white/15 flex items-center justify-center bg-[#16191C]">
            <span className="font-display text-white font-black text-sm tracking-tight">BBC</span>
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#B0FFD9] bbc-pulse" />
          </div>
          <div className="leading-tight">
            <div className="font-display text-white text-[13px] font-bold tracking-wide">Boom Barrier Co.</div>
            <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#71717A]">Infrastructure Systems</div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((n) => (
            n.to ? (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`platform-nav-${n.label.toLowerCase()}`}
                className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA] hover:text-white transition"
              >
                {n.label}
              </Link>
            ) : (
              <a
                key={n.href}
                href={n.href}
                data-testid={`platform-nav-${n.label.toLowerCase()}`}
                className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA] hover:text-white transition"
              >
                {n.label}
              </a>
            )
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/quote"
            data-testid="platform-nav-cta"
            className="hidden sm:inline-flex items-center gap-2 bg-[#00FF85] hover:bg-[#00CC6A] text-black font-semibold px-4 py-2.5 text-[11px] uppercase tracking-wider transition shadow-[0_0_18px_rgba(0,255,133,0.25)] hover:shadow-[0_0_28px_rgba(0,255,133,0.5)]"
          >
            Get a Quote
          </Link>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="platform-mobile-toggle"
            className="lg:hidden w-10 h-10 border border-white/15 text-white flex items-center justify-center"
            aria-label="Menu"
          >
            {open ? <X size={18} /> : <MenuIcon size={18} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-white/5 bg-[#0F1113]" data-testid="platform-mobile-menu">
          <nav className="px-6 py-5 flex flex-col gap-1">
            {NAV.map((n) => (
              n.to ? (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className="font-mono text-xs uppercase tracking-[0.2em] text-[#A1A1AA] hover:text-white py-3 border-b border-white/5"
                >
                  {n.label}
                </Link>
              ) : (
                <a
                  key={n.href}
                  href={n.href}
                  onClick={() => setOpen(false)}
                  className="font-mono text-xs uppercase tracking-[0.2em] text-[#A1A1AA] hover:text-white py-3 border-b border-white/5"
                >
                  {n.label}
                </a>
              )
            ))}
            <Link
              to="/quote"
              onClick={() => setOpen(false)}
              className="mt-4 inline-flex items-center justify-center bg-[#00FF85] text-black font-semibold px-4 py-3 text-xs uppercase tracking-wider hover:bg-[#00CC6A] transition"
            >
              Get a Quote
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

/* HERO — moved to ./landing/Hero.jsx */

/* ===================== INDUSTRY PROBLEM ===================== */

const PROBLEMS = [
  { icon: Boxes, label: "Same imported hardware under 12 brands" },
  { icon: Hash, label: "No way to verify what was tested" },
  { icon: MapPin, label: "Zero visibility once installed" },
  { icon: AlertTriangle, label: "Failures happen without warning" },
  { icon: Cable, label: "Disconnected from any platform" },
  { icon: Wrench, label: "Service is pure guesswork" },
  { icon: Lock, label: "Anyone can change settings on-site" },
  { icon: Activity, label: "Inconsistent quality between units" },
];

const IndustryProblem = () => (
  <section
    id="industry"
    data-testid="platform-industry"
    className="relative py-24 md:py-32 border-t border-white/5 overflow-hidden"
  >
    {/* Lateral image accent on right */}
    <div className="hidden lg:block absolute top-0 right-0 bottom-0 w-[42%] -z-10 opacity-[0.18]">
      <img src="/branded/bbc-hds.png" alt="" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/60 to-transparent" />
    </div>
    <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
      <SectionLabel id={1} label="The Problem" />
      <h2 className="font-display mt-4 text-white text-4xl md:text-5xl lg:text-6xl font-black tracking-[-0.02em] leading-[1.02] max-w-4xl">
        Why Boom Barriers
        <br />
        <span className="text-[#71717A]">Keep Failing.</span>
      </h2>
      <p className="mt-6 max-w-2xl text-[#A1A1AA] text-base md:text-lg font-body">
        Most barriers sold in India are imported clones — same hardware under 12 different brands. No real warranty, no way to verify quality, no visibility once installed. Service is guesswork. Failures are routine.
      </p>

      <div className="mt-16 grid lg:grid-cols-2 gap-8 lg:gap-6">
        {/* Generic infra — dim */}
        <div className="relative border border-white/10 bg-[#16191C] p-8 md:p-10">
          <Overline icon={X}>The Status Quo</Overline>
          <h3 className="font-display mt-3 text-white text-2xl md:text-3xl font-bold tracking-tight">
            Imported. Generic. Invisible.
          </h3>
          <ul className="mt-8 grid grid-cols-2 gap-x-4 gap-y-5">
            {PROBLEMS.map((p, i) => {
              const Icon = p.icon;
              return (
                <li key={i} className="flex items-start gap-3" data-testid={`problem-${i}`}>
                  <span className="w-7 h-7 border border-white/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Icon size={13} className="text-[#71717A]" />
                  </span>
                  <span className="text-[#A1A1AA] text-sm font-body leading-snug">{p.label}</span>
                </li>
              );
            })}
          </ul>
          <div className="mt-10 hidden md:block font-mono text-[10px] uppercase tracking-[0.2em] text-[#52525B]">
            ░░░░░░░░░░░░░░░░░ Black box hardware
          </div>
        </div>

        {/* BBC ecosystem — glowing */}
        <div className="relative border border-[#00FF85]/40 bg-gradient-to-br from-[#16191C] to-[#16191C] p-8 md:p-10 overflow-hidden">
          <div className="bbc-scan opacity-40" />
          <Overline icon={CircleDot}>The BBC Way</Overline>
          <h3 className="font-display mt-3 text-white text-2xl md:text-3xl font-bold tracking-tight">
            Engineered. Tested. <span className="text-[#00FF85]">Tracked.</span>
          </h3>

          <div className="mt-8 grid grid-cols-2 gap-3">
            {[
              { k: "QC Tested", v: "Every Unit" },
              { k: "Live Status", v: "On Your Phone" },
              { k: "QR Identity", v: "Per Barrier" },
              { k: "Service Logs", v: "Auto-Tracked" },
            ].map((it, i) => (
              <div key={i} className="bbc-glass px-4 py-3" data-testid={`bbc-trait-${i}`}>
                <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#71717A]">{it.k}</div>
                <div className="mt-1 font-display text-white text-base font-bold">{it.v}</div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-[#B0FFD9] bbc-pulse" />
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA]">
              Every barrier, a serial. Every install, a record.
            </span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ===================== PLATFORM (3-LAYER ARCHITECTURE) ===================== */

const LAYERS = [
  {
    n: "03",
    name: "BBC Trust Platform",
    icon: LayoutGrid,
    items: ["Live Status", "Service Alerts", "QC Records", "Install History", "Fleet Map"],
    desc: "Track every barrier you've installed — from your phone. Know what's online, what needs service, and what just shipped.",
  },
  {
    n: "02",
    name: "Smart Controller",
    icon: Cpu,
    items: ["Password Lock", "Cycle Counter", "Fault Detection", "LED Status", "Fail-Safe Logic"],
    desc: "The brain inside every BBC barrier. Password-protected, self-diagnosing, and ready to plug into any access setup.",
  },
  {
    n: "01",
    name: "Engineered Mechanics",
    icon: Settings2,
    items: ["BLDC Motors", "Hardened Gears", "MS / GI Body"],
    desc: "Globally-sourced components. Engineered, assembled and tested in India for our climate, traffic and on-site reality.",
  },
];

export const PlatformApproach = () => (
  <section
    id="platform"
    data-testid="platform-approach"
    className="relative py-24 md:py-32 border-t border-white/5 bg-[#13161A]"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <SectionLabel id={2} label="How BBC Is Built" />
      <h2 className="font-display mt-4 text-white text-4xl md:text-5xl lg:text-6xl font-black tracking-[-0.02em] leading-[1.02] max-w-4xl">
        Three Layers.
        <br />
        <span className="text-[#00FF85]">One Promise.</span>
      </h2>
      <p className="mt-6 max-w-2xl text-[#A1A1AA] text-base md:text-lg font-body">
        A complete barrier system — mechanical, electronic, and digital. Engineered together so nothing on-site is a black box.
      </p>

      {/* Layer stack */}
      <div className="mt-16 space-y-4">
        {LAYERS.map((l, i) => {
          const Icon = l.icon;
          const isTop = i === 0;
          return (
            <div
              key={l.n}
              data-testid={`layer-${l.n}`}
              className={`group relative border bg-gradient-to-br p-6 md:p-8 transition-all hover:-translate-y-1 ${
                isTop
                  ? "border-[#00FF85]/40 from-[#16191C] to-[#16191C] shadow-[0_0_40px_-10px_rgba(0,255,133,0.35)]"
                  : "border-white/10 from-[#16191C] to-[#13161A] hover:border-white/20"
              }`}
            >
              <div className="grid lg:grid-cols-12 gap-6 items-center">
                <div className="lg:col-span-1 flex items-center gap-4">
                  <div className={`w-14 h-14 border flex items-center justify-center ${isTop ? "border-[#00FF85]/50 bg-[#00FF85]/10" : "border-white/15 bg-white/5"}`}>
                    <Icon size={22} className={isTop ? "text-[#00FF85]" : "text-white"} />
                  </div>
                </div>
                <div className="lg:col-span-4">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#52525B]">
                    Layer {l.n}
                  </span>
                  <h3 className={`font-display mt-1.5 text-2xl md:text-3xl font-bold tracking-tight ${isTop ? "text-white" : "text-white/85"}`}>
                    {l.name}
                  </h3>
                </div>
                <div className="lg:col-span-4">
                  <p className="text-[#A1A1AA] text-sm md:text-base font-body leading-relaxed">{l.desc}</p>
                </div>
                <div className="lg:col-span-3 flex flex-wrap gap-1.5">
                  {l.items.map((it, j) => (
                    <span
                      key={j}
                      className={`font-mono text-[10px] uppercase tracking-wider px-2 py-1 border ${
                        isTop
                          ? "border-[#00FF85]/30 text-[#00FF85] bg-[#00FF85]/5"
                          : "border-white/10 text-[#A1A1AA] bg-white/[0.02]"
                      }`}
                    >
                      {it}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Highlight callout */}
      <div className="mt-12 border border-[#00FF85]/30 bg-gradient-to-r from-[#00FF85]/10 via-[#00FF85]/5 to-transparent p-8 md:p-10 relative overflow-hidden">
        <div className="bbc-scan opacity-30" />
        <div className="relative flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
          <div className="md:w-1/2">
            <Overline icon={Network}>Plug & Play</Overline>
            <h3 className="font-display mt-2 text-white text-3xl md:text-4xl font-black tracking-tight">
              Works With Your Existing Setup.
            </h3>
          </div>
          <p className="md:w-1/2 text-[#A1A1AA] text-base font-body leading-relaxed">
            RFID readers, ANPR cameras, boom controllers, loop detectors — BBC plugs into whatever you've already deployed. No vendor lock-in. No re-engineering.
          </p>
        </div>
      </div>
    </div>
  </section>
);

/* ===================== STATS RIBBON ===================== */

const STATS = [
  { v: "4", k: "Families", sub: "PRIME · ELITE · FLASH · MAX" },
  { v: "13", k: "SKUs", sub: "Cabinet + drive variants" },
  { v: "99.94%", k: "Uptime", sub: "Field deployments" },
  { v: "Every", k: "Unit Tested", sub: "Before dispatch" },
  { v: "QR", k: "On Each Barrier", sub: "Verifiable identity" },
];

export const StatsRibbon = () => (
  <section
    data-testid="platform-stats"
    className="relative py-14 md:py-20 border-t border-white/5 bg-gradient-to-b from-[#16191C] via-[#1F2326] to-[#16191C]"
  >
    <div className="absolute inset-0 bbc-grid-bg opacity-[0.06] pointer-events-none" />
    <div className="relative max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-x-3 gap-y-8">
        {STATS.map((s, i) => (
          <div
            key={i}
            data-testid={`stat-${i}`}
            className={`relative ${i > 0 ? "md:pl-6 md:border-l md:border-white/8" : ""}`}
          >
            <div className="font-display text-white text-4xl md:text-5xl lg:text-6xl font-black tracking-[-0.02em] leading-none">
              {s.v}
            </div>
            <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[#00FF85]">
              {s.k}
            </div>
            <div className="mt-1 text-[#71717A] text-xs font-body">{s.sub}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ===================== MANIFESTO BREAK ===================== */

export const Manifesto = () => (
  <section
    data-testid="platform-manifesto"
    className="relative py-24 md:py-40 border-t border-white/5 overflow-hidden"
  >
    {/* Diagonal beam bg */}
    <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#16191C] via-black to-[#181B1E]" />
    <div
      className="absolute -top-40 -left-40 w-[700px] h-[700px] rounded-full blur-3xl pointer-events-none"
      style={{ background: "radial-gradient(circle, rgba(0,255,133,0.12), transparent 60%)" }}
    />
    <div
      className="absolute -bottom-40 -right-40 w-[700px] h-[700px] rounded-full blur-3xl pointer-events-none"
      style={{ background: "radial-gradient(circle, rgba(0,255,133,0.10), transparent 60%)" }}
    />

    <div className="relative max-w-6xl mx-auto px-6 md:px-12">
      <div className="flex items-center gap-4">
        <span className="w-12 h-px bg-[#00FF85]" />
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00FF85]">
          Manifesto
        </span>
      </div>
      <p className="mt-8 font-display text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black tracking-[-0.02em] leading-[1.1]">
        We don't sell boom barriers.
        <br />
        <span className="text-[#00FF85]">
          We sell the intelligence layer that sits above them.
        </span>
      </p>
      <div className="mt-10 flex items-center gap-4">
        <span className="w-12 h-px bg-white/15" />
        <p className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-[#A1A1AA] max-w-xl">
          Built · Tested · Tracked · Serviceable — from the factory floor to the field.
        </p>
      </div>
    </div>
  </section>
);

/* ===================== (ShowcaseStrip removed per design direction) ===================== */

/* ===================== TRACEABILITY (Real QR + App Screens) ===================== */

const TrustPhoneCarousel = () => {
  const [idx, setIdx] = useState(0);
  const total = TRUST_APP_SCREENS.length;
  useEffect(() => {
    const t = setInterval(() => setIdx((v) => (v + 1) % total), 3800);
    return () => clearInterval(t);
  }, [total]);
  const slide = TRUST_APP_SCREENS[idx];

  return (
    <div className="relative">
      <div className="absolute -inset-4 bg-gradient-to-br from-[#00FF85]/20 via-[#00FF85]/10 to-transparent blur-2xl -z-10" />
      <div className="flex items-center justify-center gap-8 lg:gap-10">
        {/* Phone */}
        <div className="relative shrink-0">
          <div className="w-[230px] h-[470px] sm:w-[260px] sm:h-[530px] rounded-[2.4rem] border-[3px] border-white/15 bg-[#0F1113] overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,255,133,0.4)] relative">
            <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-5 bg-black rounded-b-2xl z-20" />
            {TRUST_APP_SCREENS.map((s, i) => (
              <img
                key={i}
                src={s.src}
                alt={s.title}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                  i === idx ? "opacity-100" : "opacity-0"
                }`}
                style={{
                  // Only trim the bottom URL/nav bar. Sides and top untouched (original
                  // screenshots already include a clean OS status bar).
                  clipPath: "inset(0 0 15% 0)",
                }}
                loading={i === 0 ? "eager" : "lazy"}
              />
            ))}
            {/* Fake iOS-style home indicator covering the bottom-trim where the Glide URL bar used to be */}
            <div className="absolute bottom-0 left-0 right-0 h-[15%] bg-[#0F1113] z-10 flex items-end justify-center pb-2">
              <span className="w-20 h-[3px] rounded-full bg-white/40" />
            </div>
          </div>
          {/* Live pulse */}
          <div className="absolute -right-2 top-6 flex items-center gap-1.5 bbc-glass px-2 py-1">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00FF85] bbc-pulse" />
            <span className="font-mono text-[9px] uppercase tracking-wider text-white/80">Live</span>
          </div>
        </div>

        {/* Caption + dots */}
        <div className="hidden sm:block flex-1 max-w-xs">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#00FF85]">
            Screen {String(idx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </div>
          <h4 className="font-display mt-3 text-white text-xl font-bold tracking-tight">{slide.title}</h4>
          <p className="mt-2 text-[#A1A1AA] text-sm font-body leading-relaxed">{slide.caption}</p>
          <div className="mt-6 flex flex-col gap-2">
            {TRUST_APP_SCREENS.map((s, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                data-testid={`trust-screen-${i}`}
                className={`flex items-center gap-3 text-left transition ${
                  i === idx ? "opacity-100" : "opacity-40 hover:opacity-80"
                }`}
              >
                <span
                  className={`w-6 h-px transition-all ${
                    i === idx ? "w-10 bg-[#00FF85]" : "bg-white/30"
                  }`}
                />
                <span className="font-mono text-[10px] uppercase tracking-wider text-white/85 truncate">
                  {s.title}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile caption */}
      <div className="sm:hidden mt-6 text-center">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#00FF85]">
          {String(idx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </div>
        <h4 className="font-display mt-2 text-white text-lg font-bold tracking-tight">{slide.title}</h4>
        <p className="mt-2 text-[#A1A1AA] text-sm font-body">{slide.caption}</p>
        <div className="mt-4 flex justify-center gap-1.5">
          {TRUST_APP_SCREENS.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              className={`w-2 h-2 rounded-full transition ${i === idx ? "bg-[#00FF85]" : "bg-white/20"}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export const Traceability = () => (
  <section
    id="traceability"
    data-testid="platform-traceability"
    className="relative py-24 md:py-32 border-t border-white/5"
  >
    <div className="absolute inset-0 bbc-grid-bg opacity-[0.07] pointer-events-none" />
    <div className="relative max-w-7xl mx-auto px-6 md:px-12">
      <SectionLabel id={3} label="BBC Trust System" />
      <h2 className="font-display mt-4 text-white text-4xl md:text-5xl lg:text-6xl font-black tracking-[-0.02em] leading-[1.02] max-w-5xl">
        Every Barrier Has
        <br />
        <span className="text-[#B0FFD9]">a Digital Identity.</span>
      </h2>
      <p className="mt-6 max-w-2xl text-[#A1A1AA] text-base md:text-lg font-body">
        A unique serial number, a QR sticker, and a complete factory record — bound to every barrier we ship. Scan, search, and verify in seconds.
      </p>

      {/* Three-step flow */}
      <div className="mt-14 grid sm:grid-cols-3 gap-3">
        {[
          { n: "01", t: "Scan The QR", d: "Point your phone camera at the QR sticker on the barrier — no app store needed." },
          { n: "02", t: "Enter Serial Number", d: "Each barrier carries a unique serial. Type or scan it to pull the record." },
          { n: "03", t: "See The Full History", d: "QC video, components, motor data, install date, service log — all live." },
        ].map((s, i) => (
          <div key={i} data-testid={`trust-step-${i}`} className="border border-white/10 bg-[#16191C] p-6 relative">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#00FF85]">Step {s.n}</span>
            <h3 className="font-display mt-3 text-white text-xl font-bold tracking-tight">{s.t}</h3>
            <p className="mt-2 text-[#A1A1AA] text-sm font-body leading-relaxed">{s.d}</p>
          </div>
        ))}
      </div>

      {/* QR + Phone */}
      <div className="mt-16 grid lg:grid-cols-2 gap-12 items-center">
        {/* QR card */}
        <div className="border border-[#00FF85]/30 bg-gradient-to-br from-[#00FF85]/[0.08] to-transparent p-8 md:p-10">
          <Overline icon={QrCode}>Try It Yourself</Overline>
          <h3 className="font-display mt-4 text-white text-2xl md:text-3xl font-bold tracking-tight">
            Scan This QR.
          </h3>
          <p className="mt-3 text-[#A1A1AA] text-sm md:text-base font-body leading-relaxed">
            This is the same QR sticker we put on every BBC barrier. Scan it now — you'll see exactly what your customer sees on install day.
          </p>

          {/* QR + spec tiles inline — uses the empty space beside the QR instead of stacking below it */}
          <div className="mt-7 grid grid-cols-[auto_1fr] gap-5 md:gap-6 items-center">
            <a
              href={TRUST_QR_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="trust-qr-link"
              className="rounded-2xl overflow-hidden border border-white/15 hover:border-[#00FF85] bg-white p-3 transition hover:scale-[1.02] shadow-[0_0_40px_-10px_rgba(0,255,133,0.5)]"
            >
              <img
                src={TRUST_QR_URL}
                alt="QR code for BBC Trust verification — scan to view factory test record of this barrier"
                className="block w-40 h-40 md:w-44 md:h-44 object-contain"
              />
            </a>
            <div className="grid grid-cols-1 gap-2">
              {[
                ["Per Unit", "Unique QR"],
                ["No App", "Install via scan"],
                ["QC Video", "Per barrier"],
                ["Component IDs", "Fully logged"],
              ].map(([k, v], i) => (
                <div key={i} className="border border-white/5 bg-white/[0.02] px-3 py-2">
                  <div className="font-mono text-[9px] uppercase tracking-wider text-[#52525B]">{k}</div>
                  <div className="mt-0.5 text-white text-xs font-semibold">{v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Phone carousel */}
        <TrustPhoneCarousel />
      </div>
    </div>
  </section>
);

/* ===================== SMART CONTROLLER — INFRASTRUCTURE OS LAYER ===================== */

const ARCHITECTURE_BLOCKS = [
  {
    n: "01",
    layer: "Infrastructure Intelligence",
    accent: "#00FF85",
    icon: ShieldCheck,
    cards: [
      {
        icon: Lock,
        title: "Protected Infrastructure Access",
        desc: "Password-protected engineering access prevents unauthorized parameter changes.",
      },
      {
        icon: ShieldCheck,
        title: "Local Fail-Safe Logic",
        desc: "Critical protection logic stays active even when network connectivity is lost.",
      },
      {
        icon: Radio,
        title: "Secure Device Pairing",
        desc: "Remotes, gateways and system components are uniquely mapped to each deployment.",
      },
      {
        icon: Hash,
        title: "Deployment Identity",
        desc: "Every controller, gateway and barrier carries a traceable infrastructure identity.",
      },
    ],
  },
  {
    n: "02",
    layer: "Live Diagnostics Layer",
    accent: "#B0FFD9",
    icon: Activity,
    cards: [
      {
        icon: Gauge,
        title: "Real-Time Cycle Analytics",
        desc: "Continuously tracks barrier movement cycles and operational behaviour.",
      },
      {
        icon: Activity,
        title: "Predictive Diagnostics",
        desc: "Identifies abnormal operation patterns before field failures occur.",
      },
      {
        icon: Zap,
        title: "LED Status Blade",
        desc: "Instant field-visible system health through integrated LED infrastructure indicators.",
      },
      {
        icon: AlertTriangle,
        title: "Intelligent Fault Monitoring",
        desc: "Motor, controller and movement anomalies are continuously monitored.",
      },
    ],
  },
  {
    n: "03",
    layer: "Infrastructure Connectivity",
    accent: "#00FF85",
    icon: Network,
    cards: [
      {
        icon: Network,
        title: "Controller-Agnostic Architecture",
        desc: "Designed to interface across multiple motion-control ecosystems.",
      },
      {
        icon: Cable,
        title: "RS485 / Ethernet Ready",
        desc: "Built for modern RFID, ANPR, parking and access-control infrastructure.",
      },
      {
        icon: LayoutGrid,
        title: "Integrated HMI System",
        desc: "Local setup, diagnostics and service without external programming tools.",
      },
      {
        icon: Wifi,
        title: "Cloud-Ready Infrastructure",
        desc: "Deployment data, diagnostics and lifecycle intelligence sync into the BBC platform.",
      },
    ],
  },
  {
    n: "04",
    layer: "BBC Deployment OS",
    accent: "#00FF85",
    icon: Workflow,
    cards: [
      {
        icon: QrCode,
        title: "QR-Linked Traceability",
        desc: "Every deployment is mapped to QC records, hardware identity and service history.",
      },
      {
        icon: Wrench,
        title: "Service-Aware Infrastructure",
        desc: "Field systems maintain lifecycle intelligence across deployment and maintenance.",
      },
      {
        icon: MapPin,
        title: "Smart Deployment Visibility",
        desc: "Online status, fault alerts and deployment analytics accessible from a unified platform.",
      },
      {
        icon: Workflow,
        title: "Infrastructure Intelligence Layer",
        desc: "BBC transforms commodity hardware into connected, service-aware infrastructure systems.",
      },
    ],
  },
];

/* Cinematic controller stack — left visual */
export const ControllerStackVisual = () => {
  const [cycle, setCycle] = useState(48230);
  useEffect(() => {
    const t = setInterval(() => setCycle((v) => v + 1), 1700);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="relative aspect-[3/4] w-full max-w-md mx-auto lg:max-w-none">
      {/* Outer glow */}
      <div className="absolute -inset-4 bg-gradient-to-br from-[#00FF85]/25 via-[#00FF85]/15 to-[#B0FFD9]/10 blur-3xl -z-10" />

      {/* Frame */}
      <div className="relative h-full w-full border border-white/10 bg-gradient-to-b from-[#16191C] via-[#16191C] to-black overflow-hidden">
        {/* PCB grid */}
        <div className="absolute inset-0 bbc-grid-bg opacity-25" />

        {/* SVG circuit traces */}
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 533" fill="none" preserveAspectRatio="xMidYMid slice">
          <defs>
            <linearGradient id="trace1" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#00FF85" stopOpacity="0" />
              <stop offset="50%" stopColor="#00FF85" stopOpacity="0.7" />
              <stop offset="100%" stopColor="#00FF85" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="trace2" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#00FF85" stopOpacity="0" />
              <stop offset="50%" stopColor="#00FF85" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#00FF85" stopOpacity="0" />
            </linearGradient>
          </defs>
          {/* Horizontal traces */}
          <path d="M 40 180 L 200 180 L 200 230 L 360 230" stroke="url(#trace1)" strokeWidth="1" />
          <path d="M 360 280 L 220 280 L 220 320 L 40 320" stroke="url(#trace1)" strokeWidth="1" />
          {/* Vertical traces */}
          <path d="M 80 130 L 80 380" stroke="url(#trace2)" strokeWidth="1" />
          <path d="M 320 150 L 320 380" stroke="url(#trace2)" strokeWidth="1" />
          {/* Node dots */}
          <circle cx="200" cy="180" r="3" fill="#00FF85" />
          <circle cx="200" cy="230" r="3" fill="#00FF85" />
          <circle cx="220" cy="280" r="3" fill="#00FF85" />
          <circle cx="220" cy="320" r="3" fill="#00FF85" />
          <circle cx="80" cy="180" r="2" fill="#B0FFD9" />
          <circle cx="320" cy="230" r="2" fill="#B0FFD9" />
        </svg>

        {/* Scanline */}
        <div className="bbc-scan opacity-20" />

        {/* TOP — HMI display panel */}
        <div className="absolute top-[6%] left-[8%] right-[8%]">
          <div className="bbc-glass p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#00FF85] bbc-pulse" />
                <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/80">SYS.READY</span>
              </div>
              <span className="font-mono text-[9px] text-white/40">v2.1.4</span>
            </div>
            <div className="mt-2.5 grid grid-cols-3 gap-2">
              {[
                ["CYC", cycle.toLocaleString()],
                ["VOLT", "24.1V"],
                ["TEMP", "32°C"],
              ].map(([k, v], i) => (
                <div key={i}>
                  <div className="font-mono text-[8px] text-[#52525B]">{k}</div>
                  <div className="font-mono text-[10px] text-[#00FF85]">{v}</div>
                </div>
              ))}
            </div>
            <div className="mt-2 font-mono text-[8px] text-[#71717A]">// HMI · BBC.CTRL.PRO</div>
          </div>
        </div>

        {/* MID — controller PCB chip */}
        <div className="absolute top-[40%] left-1/2 -translate-x-1/2 -translate-y-1/2">
          <div className="relative w-32 h-32 border border-[#00FF85]/40 bg-[#16191C] flex items-center justify-center shadow-[0_0_40px_-10px_rgba(0,255,133,0.6)]">
            <Cpu size={36} className="text-[#00FF85]" />
            {/* Corner pins */}
            <span className="absolute -top-1.5 left-2 w-1.5 h-1.5 bg-[#00FF85]" />
            <span className="absolute -top-1.5 right-2 w-1.5 h-1.5 bg-[#00FF85]" />
            <span className="absolute -bottom-1.5 left-2 w-1.5 h-1.5 bg-[#00FF85]" />
            <span className="absolute -bottom-1.5 right-2 w-1.5 h-1.5 bg-[#00FF85]" />
            <span className="absolute -left-1.5 top-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-[#B0FFD9] bbc-pulse" />
            <span className="absolute -right-1.5 top-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-[#B0FFD9] bbc-pulse" />
            <div className="absolute top-1 left-2 font-mono text-[7px] uppercase tracking-wider text-white/40">BBC.CTRL</div>
          </div>
        </div>

        {/* Floating left telemetry card */}
        <div className="absolute top-[28%] left-[4%] bbc-glass px-2.5 py-2">
          <div className="font-mono text-[8px] uppercase tracking-wider text-[#71717A]">SIGNAL</div>
          <div className="mt-0.5 flex items-end gap-0.5 h-4">
            {[0.4, 0.6, 0.8, 1, 0.9].map((h, i) => (
              <span
                key={i}
                className="w-1 bg-[#00FF85] bbc-pulse"
                style={{ height: `${h * 100}%`, animationDelay: `${i * 0.2}s` }}
              />
            ))}
          </div>
        </div>

        {/* Floating right telemetry card */}
        <div className="absolute top-[55%] right-[4%] bbc-glass px-2.5 py-2 text-right">
          <div className="font-mono text-[8px] uppercase tracking-wider text-[#71717A]">UPTIME</div>
          <div className="mt-0.5 font-mono text-[11px] text-[#00FF85]">99.94%</div>
        </div>

        {/* BOTTOM — LED status blade */}
        <div className="absolute bottom-[14%] left-[10%] right-[10%]">
          <div className="font-mono text-[8px] uppercase tracking-[0.25em] text-[#52525B] mb-2">
            LED.STATUS.BLADE
          </div>
          <div className="flex gap-1 h-2">
            {Array.from({ length: 14 }).map((_, i) => (
              <span
                key={i}
                className="flex-1 bg-[#00FF85] bbc-pulse"
                style={{
                  animationDelay: `${i * 0.12}s`,
                  opacity: 0.4 + (i % 3) * 0.2,
                }}
              />
            ))}
          </div>
          <div className="mt-2 flex items-center justify-between font-mono text-[8px] text-[#71717A]">
            <span>ONLINE</span>
            <span>HEALTHY</span>
          </div>
        </div>

        {/* Bottom-right label */}
        <div className="absolute bottom-3 right-3 font-mono text-[8px] uppercase tracking-[0.25em] text-[#52525B]">
          / BBC.OS
        </div>
      </div>
    </div>
  );
};

/* Engineering summary card — sits below the controller visual to balance the section */
const ControllerEngineeringSummary = () => {
  const SPECS = [
    { k: "Operating Voltage", v: "24V DC" },
    { k: "Logic Voltage", v: "3.3V / 5V" },
    { k: "Comms", v: "RS485 · Ethernet" },
    { k: "Firmware Lock", v: "Password-Protected" },
    { k: "Fail-Safe Mode", v: "Auto Engage" },
    { k: "Operating Temp", v: "−10°C to 65°C" },
  ];
  return (
    <div
      data-testid="controller-summary"
      className="relative border border-white/10 bg-[#16191C] overflow-hidden"
    >
      {/* Top accent line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00FF85]/60 to-transparent" />

      {/* Header bar */}
      <div className="flex items-center justify-between px-5 md:px-6 py-4 border-b border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 border border-[#00FF85]/40 bg-[#00FF85]/10 flex items-center justify-center">
            <CircuitBoard size={15} className="text-[#00FF85]" />
          </div>
          <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#00FF85]">
              /// — Engineering Summary
            </div>
            <h3 className="font-display mt-0.5 text-white text-base md:text-lg font-bold tracking-tight">
              BBC.CTRL.PRO · System Specification
            </h3>
          </div>
        </div>
        <span className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider text-[#B0FFD9]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#B0FFD9] bbc-pulse" />
          Verified
        </span>
      </div>

      {/* Spec grid */}
      <div className="grid grid-cols-2 gap-px bg-white/5">
        {SPECS.map(({ k, v }, i) => (
          <div
            key={i}
            data-testid={`controller-summary-${i}`}
            className="bg-[#16191C] px-5 md:px-6 py-4"
          >
            <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#52525B]">
              {k}
            </div>
            <div className="mt-1.5 font-display text-white text-sm md:text-base font-bold tracking-tight leading-tight">
              {v}
            </div>
          </div>
        ))}
      </div>

      {/* Footer caption */}
      <div className="flex items-center justify-between px-5 md:px-6 py-3.5 border-t border-white/5 bg-[#13161A]">
        <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#71717A]">
          Spec sheet bound to serial · QR verifiable
        </span>
        <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#00FF85]">
          / BBC.CTRL.v2.1.4
        </span>
      </div>
    </div>
  );
};



const SmartController = () => (
  <section
    id="controller"
    data-testid="platform-controller"
    className="relative py-24 md:py-32 border-t border-white/5 bg-[#13161A] overflow-hidden"
  >
    {/* Ambient bg */}
    <div className="absolute inset-0 bbc-grid-bg opacity-[0.06] pointer-events-none" />
    <div className="absolute top-0 left-1/3 w-[600px] h-[600px] rounded-full bg-[#00FF85]/10 blur-3xl pointer-events-none" />

    <div className="relative max-w-7xl mx-auto px-6 md:px-12">
      {/* Header + cinematic visual side-by-side — fills the wasted right-side space of the heading */}
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
        <div className="lg:col-span-7">
          <SectionLabel id={4} label="Beyond Motion Control" />
          <h2 className="font-display mt-4 text-white text-4xl md:text-5xl lg:text-6xl font-black tracking-[-0.02em] leading-[1.02]">
            A Real Brain Inside
            <br />
            <span className="text-[#00FF85]">Every Barrier.</span>
          </h2>
          <p className="mt-6 max-w-2xl text-[#A1A1AA] text-base md:text-lg font-body leading-relaxed">
            BBC barriers are built around an intelligent controller architecture designed for diagnostics, deployment control, traceability and smart infrastructure integration.
          </p>
          <div className="mt-8">
            <ControllerEngineeringSummary />
          </div>
        </div>
        <div className="lg:col-span-5">
          <ControllerStackVisual />
        </div>
      </div>

      {/* 4 architecture blocks — full-width 2×2 grid below the header/visual row */}
      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-3">
          {ARCHITECTURE_BLOCKS.map((b) => {
            const HeaderIcon = b.icon;
            return (
              <div
                key={b.n}
                data-testid={`arch-block-${b.n}`}
                className="group relative border border-white/10 bg-[#16191C] hover:border-white/15 transition-all overflow-hidden"
              >
                {/* Top accent line */}
                <div
                  className="absolute top-0 left-0 right-0 h-px opacity-60"
                  style={{
                    background: `linear-gradient(90deg, transparent, ${b.accent}, transparent)`,
                  }}
                />

                {/* Block header */}
                <div className="px-6 md:px-7 pt-6 pb-5 flex items-center gap-4 border-b border-white/5">
                  <div
                    className="w-11 h-11 border flex items-center justify-center shrink-0"
                    style={{ borderColor: `${b.accent}50`, background: `${b.accent}12` }}
                  >
                    <HeaderIcon size={18} style={{ color: b.accent }} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-3">
                      <span
                        className="font-mono text-[10px] uppercase tracking-[0.25em]"
                        style={{ color: b.accent }}
                      >
                        Layer.{b.n}
                      </span>
                      <span className="w-6 h-px bg-white/15" />
                      <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#52525B]">
                        Active
                      </span>
                    </div>
                    <h3 className="font-display mt-1 text-white text-xl md:text-2xl font-bold tracking-tight">
                      {b.layer}
                    </h3>
                  </div>
                  <span
                    className="ml-auto hidden md:inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider shrink-0"
                    style={{ color: b.accent }}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full bbc-pulse"
                      style={{ background: b.accent }}
                    />
                    Live
                  </span>
                </div>

                {/* Cards grid */}
                <div className="px-6 md:px-7 pb-7 pt-5">
                  <div className="grid grid-cols-1 gap-px bg-white/5">
                    {b.cards.map((c, i) => {
                      const Icon = c.icon;
                      return (
                        <div
                          key={i}
                          data-testid={`arch-card-${b.n}-${i}`}
                          className="bg-[#16191C] p-4 hover:bg-[#16191C] transition group/card"
                        >
                          <div className="flex items-start gap-3">
                            <div
                              className="w-8 h-8 border flex items-center justify-center shrink-0 transition"
                              style={{
                                borderColor: "rgba(255,255,255,0.08)",
                              }}
                            >
                              <Icon
                                size={13}
                                className="text-white/70 group-hover/card:text-white transition"
                                style={{ color: `${b.accent}` }}
                              />
                            </div>
                            <div className="min-w-0">
                              <h4 className="font-display text-white text-[13px] font-bold tracking-tight leading-tight">
                                {c.title}
                              </h4>
                              <p className="mt-1.5 text-[#A1A1AA] text-[11.5px] font-body leading-relaxed">
                                {c.desc}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

      {/* Closing positioning line */}
      <div className="mt-14 border-l-2 border-[#00FF85] bg-[#00FF85]/[0.05] px-6 py-5 max-w-3xl">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#00FF85]">
          / Positioning
        </div>
        <p className="mt-2 text-white text-base md:text-lg font-body leading-relaxed">
          BBC barriers are not just motor-driven machines.{" "}
          <span className="text-[#A1A1AA]">
            They're intelligent, traceable and service-aware infrastructure systems — an intelligence layer built above commodity motion hardware.
          </span>
        </p>
      </div>
    </div>
  </section>
);

/* ===================== PREMIUM CANOPY (merged into Cabinet Options) ===================== */

/* Portfolio, CabinetOptions and SegmentedPathways now live in ./landing/ */

/* ===================== CABINET OPTIONS — moved to ./landing/CabinetOptions.jsx ===================== */




/* ===================== SEGMENTED PATHWAYS — moved to ./landing/SegmentedPathways.jsx ===================== */



/* ===================== FUTURE VISION ===================== */

const ECOSYSTEMS = [
  { icon: Building2, label: "Residential Societies" },
  { icon: Factory, label: "Industrial Sites" },
  { icon: Truck, label: "Logistics Hubs" },
  { icon: Plane, label: "Airports" },
  { icon: LayoutGrid, label: "Corporate Parks" },
  { icon: Zap, label: "Toll Plazas" },
  { icon: Network, label: "Parking Facilities" },
];

const FutureVision = () => (
  <section id="vision" data-testid="platform-vision" className="relative py-24 md:py-32 border-t border-white/5 overflow-hidden">
    <div className="absolute inset-0 -z-10">
      <img src={MEDIA.smartcity} alt="" className="w-full h-full object-cover opacity-40" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0F1113] via-[#0F1113]/60 to-[#0F1113]" />
    </div>
    <div className="relative max-w-7xl mx-auto px-6 md:px-12">
      <SectionLabel id={10} label="Where BBC Lives" />
      <h2 className="font-display mt-4 text-white text-4xl md:text-5xl lg:text-7xl font-black tracking-[-0.02em] leading-[1.02] max-w-5xl">
        Built for Every
        <br />
        <span className="text-[#B0FFD9]">Kind of Site.</span>
      </h2>

      <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {ECOSYSTEMS.map((e, i) => {
          const Icon = e.icon;
          return (
            <div
              key={i}
              data-testid={`ecosystem-${i}`}
              className="bbc-glass p-5 hover:border-white/20 transition group"
            >
              <Icon size={22} className="text-white group-hover:text-[#B0FFD9] transition" />
              <div className="mt-4 font-display text-white text-sm font-bold tracking-tight">{e.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);


/* FIELD EVIDENCE — moved to ./landing/FieldEvidenceVideos.jsx */

/* ===================== FINAL CTA ===================== */

export const FinalCTA = () => (
  <section
    id="cta"
    data-testid="platform-cta"
    className="relative py-24 md:py-40 border-t border-white/5 overflow-hidden"
  >
    <div className="absolute inset-0 -z-10">
      <img src={MEDIA.metal} alt="" className="w-full h-full object-cover opacity-30" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0F1113]/80 via-[#0F1113]/70 to-[#0F1113]" />
    </div>
    <div className="absolute inset-0 bbc-grid-bg opacity-[0.08] -z-10" />

    <div className="relative max-w-5xl mx-auto px-6 md:px-12 text-center">
      <Overline icon={Radio}>Get Started</Overline>
      <h2 className="font-display mt-6 text-white text-5xl md:text-6xl lg:text-7xl font-black tracking-[-0.02em] leading-[1.02]">
        Ready to Deploy
        <br />
        <span className="text-[#00FF85]">Smarter Barriers?</span>
      </h2>
      <p className="mt-8 text-[#A1A1AA] text-base md:text-lg font-body max-w-2xl mx-auto">
        Talk to our team about your site, your traffic, and your installation timeline. We'll spec the right barrier and get you a quote.
      </p>
      <div className="mt-12 flex flex-wrap gap-3 justify-center">
        <ButtonPrimary lead="dealer" source="final-cta" testId="platform-cta-partner">Become a Dealer</ButtonPrimary>
        <ButtonGhost lead="sales" source="final-cta" testId="platform-cta-demo">Talk to Sales</ButtonGhost>
      </div>
    </div>
  </section>
);

/* ===================== FOOTER ===================== */

export const PlatformFooter = () => (
  <footer data-testid="platform-footer" className="border-t border-white/5 bg-[#0F1113]">
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-14">
      <div className="grid md:grid-cols-3 gap-10">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 border border-white/15 flex items-center justify-center bg-[#16191C]">
              <span className="font-display text-white font-black text-sm">BBC</span>
            </div>
            <div>
              <div className="font-display text-white text-sm font-bold">Boom Barrier Company</div>
              <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#71717A]">Infrastructure Systems</div>
            </div>
          </div>
          <p className="mt-6 text-[#71717A] text-sm font-body max-w-sm leading-relaxed">
            Smart access infrastructure systems for modern mobility. Traceable. Service-aware. Controller-agnostic.
          </p>
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#52525B] mb-5">Platform</div>
          <ul className="space-y-3">
            {NAV.map((n) => (
              <li key={n.to || n.href}>
                {n.to ? (
                  <Link to={n.to} className="text-[#A1A1AA] hover:text-white text-sm font-body transition">{n.label}</Link>
                ) : (
                  <a href={n.href} className="text-[#A1A1AA] hover:text-white text-sm font-body transition">{n.label}</a>
                )}
              </li>
            ))}
            <li>
              <Link to="/quote" className="text-[#A1A1AA] hover:text-white text-sm font-body transition">Get a Quote</Link>
            </li>
          </ul>
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#52525B] mb-5">Contact</div>
          <div className="text-[#A1A1AA] text-sm font-body space-y-2">
            <div>sales@boombarriercompany.com</div>
            <div>+91 87963 83008</div>
            <div className="text-[#71717A]">Plot 5/37, UIT Sector 5, Bhiwadi, Rajasthan 301019</div>
          </div>
        </div>
      </div>
      <div className="mt-14 pt-6 border-t border-white/5 flex flex-col sm:flex-row justify-between gap-3 text-[10px] uppercase tracking-[0.22em] font-mono text-[#52525B]">
        <span>© {new Date().getFullYear()} Boom Barrier Company · All systems operational</span>
        <span>boombarriercompany.com</span>
      </div>
    </div>
  </footer>
);

/* ===================== MAIN ===================== */

/* ===================== LEAD CAPTURE MODAL ===================== */

export const LeadCaptureModal = () => {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("sales");
  const [source, setSource] = useState("platform");
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const handler = (e) => {
      const detail = (e && e.detail) || {};
      setType(detail.type === "dealer" ? "dealer" : "sales");
      setSource(detail.source || "platform");
      setStatus("idle");
      setErrorMsg("");
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
      setOpen(true);
    };
    window.addEventListener("bbc:open-lead", handler);
    return () => window.removeEventListener("bbc:open-lead", handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMsg("");
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${apiBase}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          company: form.company.trim() || null,
          message: form.message.trim() || null,
          type,
          source,
        }),
      });
      if (!res.ok) {
        let detail = "Could not submit. Please try again.";
        try {
          const data = await res.json();
          if (typeof data.detail === "string") detail = data.detail;
          else if (Array.isArray(data.detail)) detail = "Please check the form fields.";
        } catch (parseErr) {
          // Response body wasn't JSON — fall back to the generic detail message.
          console.warn("Lead API returned non-JSON error body", parseErr);
        }
        setErrorMsg(detail);
        setStatus("error");
        return;
      }
      setStatus("success");
    } catch (networkErr) {
      console.error("Lead submit network error", networkErr);
      setErrorMsg("Network error. Please try again.");
      setStatus("error");
    }
  };

  if (!open) return null;

  const isDealer = type === "dealer";
  const heading = isDealer ? "Partner With BBC" : "Talk to Sales";
  const sub = isDealer
    ? "Become a BBC channel partner. Share your territory, volume, and current product mix — we'll set up an intro call."
    : "Tell us about your site, traffic profile, and timeline. Our team will spec the right barrier and get back within one business day.";

  return (
    <div
      data-testid="lead-modal"
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
    >
      <div
        className="absolute inset-0 bg-black/92 backdrop-blur-md"
        onClick={() => setOpen(false)}
      />
      <div className="relative w-full max-w-2xl overflow-hidden border border-[#00FF85]/30 bg-[#0F1113] shadow-2xl shadow-[#00FF85]/10">
        {/* corner brackets */}
        <span className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-[#00FF85] z-10 pointer-events-none" />
        <span className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 border-[#00FF85] z-10 pointer-events-none" />
        <span className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2 border-[#00FF85] z-10 pointer-events-none" />
        <span className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 border-[#00FF85] z-10 pointer-events-none" />

        {/* Header */}
        <div className="relative flex items-start justify-between gap-6 px-7 py-6 border-b border-white/5 bg-[#16191C]">
          <div>
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#00FF85]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00FF85] bbc-pulse" />
              {isDealer ? "/// — DEALER ENQUIRY" : "/// — SALES ENQUIRY"}
            </div>
            <h3 className="font-display mt-1.5 text-white text-2xl md:text-3xl font-black tracking-[-0.02em]">
              {heading}
            </h3>
            <p className="mt-1.5 text-[#A1A1AA] text-sm font-body leading-relaxed max-w-md">{sub}</p>
          </div>
          <button
            onClick={() => setOpen(false)}
            data-testid="lead-modal-close"
            aria-label="Close"
            className="shrink-0 w-9 h-9 border border-white/10 hover:border-[#00FF85] text-white/70 hover:text-[#00FF85] flex items-center justify-center transition"
          >
            <X size={18} />
          </button>
        </div>

        {/* Type switcher */}
        <div className="px-7 pt-5 flex gap-2">
          {[
            { id: "sales", label: "Talk to Sales" },
            { id: "dealer", label: "Become a Dealer" },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setType(t.id)}
              data-testid={`lead-type-${t.id}`}
              className={`px-4 py-2 border font-mono text-[10px] uppercase tracking-[0.22em] transition ${
                type === t.id
                  ? "bg-[#00FF85] border-[#00FF85] text-black"
                  : "border-white/15 text-white/70 hover:border-[#00FF85]/60 hover:text-[#00FF85]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        {status === "success" ? (
          <div className="px-7 py-12 text-center" data-testid="lead-modal-success">
            <div className="mx-auto w-14 h-14 border-2 border-[#00FF85] flex items-center justify-center mb-5">
              <ShieldCheck className="text-[#00FF85]" size={26} />
            </div>
            <h4 className="font-display text-white text-2xl font-black tracking-tight">Logged. Thank you.</h4>
            <p className="mt-3 text-[#A1A1AA] text-sm font-body max-w-md mx-auto">
              Your enquiry has been recorded against ref{" "}
              <span className="text-[#00FF85] font-mono">BBC-{Math.random().toString(36).slice(2, 8).toUpperCase()}</span>.
              A team member will reach out within one business day.
            </p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="lead-modal-close-success"
              className="mt-7 inline-flex items-center gap-3 bg-[#00FF85] hover:bg-[#00CC6A] text-black font-semibold px-6 py-3 text-xs uppercase tracking-wider transition"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-7 pb-7 pt-5 space-y-4" data-testid="lead-modal-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full Name *" required>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={onChange("name")}
                  data-testid="lead-input-name"
                  placeholder="Rajesh Kumar"
                  className="w-full bg-[#16191C] border border-white/10 focus:border-[#00FF85] outline-none px-3 py-3 text-white text-sm font-body transition"
                />
              </Field>
              <Field label={isDealer ? "Company / Firm *" : "Company"} required={isDealer}>
                <input
                  type="text"
                  required={isDealer}
                  value={form.company}
                  onChange={onChange("company")}
                  data-testid="lead-input-company"
                  placeholder={isDealer ? "Kumar Automation Pvt Ltd" : "Optional"}
                  className="w-full bg-[#16191C] border border-white/10 focus:border-[#00FF85] outline-none px-3 py-3 text-white text-sm font-body transition"
                />
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Email *" required>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={onChange("email")}
                  data-testid="lead-input-email"
                  placeholder="rajesh@firm.in"
                  className="w-full bg-[#16191C] border border-white/10 focus:border-[#00FF85] outline-none px-3 py-3 text-white text-sm font-body transition"
                />
              </Field>
              <Field label="Phone *" required>
                <input
                  type="tel"
                  required
                  value={form.phone}
                  onChange={onChange("phone")}
                  data-testid="lead-input-phone"
                  placeholder="+91 98xxxxxxxx"
                  className="w-full bg-[#16191C] border border-white/10 focus:border-[#00FF85] outline-none px-3 py-3 text-white text-sm font-body transition"
                />
              </Field>
            </div>

            <Field label={isDealer ? "Territory, volume, current brands" : "Site, traffic & timeline"}>
              <textarea
                rows={3}
                value={form.message}
                onChange={onChange("message")}
                data-testid="lead-input-message"
                placeholder={isDealer
                  ? "e.g. We cover Pune + Nashik, 30–40 barrier installs / yr, currently selling Brand X."
                  : "e.g. Toll plaza near Bengaluru, 4 lanes, 24/7 traffic, install by Q3."
                }
                className="w-full bg-[#16191C] border border-white/10 focus:border-[#00FF85] outline-none px-3 py-3 text-white text-sm font-body transition resize-none"
              />
            </Field>

            {status === "error" && (
              <div
                className="flex items-start gap-2 border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm font-body text-red-300"
                data-testid="lead-modal-error"
              >
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <span>{errorMsg || "Could not submit. Please try again."}</span>
              </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#52525B] max-w-xs">
                By submitting you agree to be contacted by BBC for this enquiry.
              </p>
              <button
                type="submit"
                disabled={status === "submitting"}
                data-testid="lead-modal-submit"
                className="group inline-flex items-center gap-3 bg-[#00FF85] hover:bg-[#00CC6A] disabled:opacity-60 disabled:cursor-wait text-black font-semibold px-6 py-3 text-xs uppercase tracking-wider transition-all shadow-[0_0_24px_rgba(0,255,133,0.35)] hover:shadow-[0_0_36px_rgba(0,255,133,0.6)]"
              >
                {status === "submitting" ? "Submitting…" : (isDealer ? "Submit Dealer Enquiry" : "Submit Enquiry")}
                <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA]">
      {label}{required && <span className="text-[#00FF85]"> *</span>}
    </span>
    <div className="mt-1.5">{children}</div>
  </label>
);

export default function PlatformLanding() {
  return (
    <div
      data-testid="platform-root"
      className="min-h-screen bg-[#0F1113] text-white font-body antialiased selection:bg-[#00FF85]/40 selection:text-white"
    >
      <PlatformNavbar />
      <main>
        <Hero />
        <StatsRibbon />
        <IndustryProblem />
        <PlatformApproach />
        <Traceability />
        <SmartController />
        <Manifesto />
        <Portfolio />
        <FieldEvidenceVideos />
        <SegmentedPathways />
        <FutureVision />
        <FinalCTA />
      </main>
      <PlatformFooter />
      <LeadCaptureModal />
    </div>
  );
}
